// Types shared between the browser and the server.
import type { ExperienceLevel } from './experience.js';
import type { SnakeDir, SnakeEnd, SnakeGameView } from './games/snake/index.js';
import type { TetraxEnd, TetraxGameView } from './games/tetrax/index.js';
import type { ChessEnd } from './games/chess/index.js';

export type Role = 'player' | 'moderator' | 'admin';

export interface PublicUser {
  id: number;
  username: string;
  displayName: string;
  role: Role;
}

export interface AdminUserView extends PublicUser {
  mustChangePassword: boolean;
  isLocked: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  /** How the user joined: added by the administrator (YP), join link or invite code. */
  joinMethod: JoinMethod;
  /** Invite batch label when the user registered with an invite code. */
  inviteBatch: string | null;
  /** The administrator has allowed the radio for this player (decision 2026-09-29). */
  musicAllowed: boolean;
  /** The player may run the bingo (2026-10-01). */
  bingoHost: boolean;
}

export type JoinMethod = 'admin' | 'link' | 'code' | 'guest';
export type InviteKind = 'register' | 'guest';

/** Radio stream settings (decision 2026-09-29). */
export interface RadioSettings {
  enabled: boolean;
  name: string;
  /** Icecast stream address, https only. */
  url: string;
  /** Public player at /kuuntele/ for anyone with the link (decision 2026-09-29). */
  publicPlayer: boolean;
  /**
   * Reserved (decision 2026-09-29): one-time guest links (3 h) handed out with the public player.
   * Not in use yet; the player has a hidden place for them.
   */
  publicGuestOffer: boolean;
  /**
   * Guests who arrive while this is on hear the radio for their whole visit (decision
   * 2026-09-29); switching it off affects only new guests. No separate permissions for guests.
   */
  guestRadio: boolean;
}

/** A valid one-time link (join or password reset). The link itself is shown only once. */
export interface JoinLinkView {
  id: number;
  purpose: 'join' | 'reset';
  username: string;
  /** Existing user (reset link). */
  displayName: string | null;
  role: Role;
  createdAt: number;
  expiresAt: number;
}

export interface InviteBatchView {
  id: number;
  label: string;
  /** Registration codes or one-time guest codes (decision 2026-09-29). */
  kind: InviteKind;
  status: 'active' | 'paused';
  createdAt: number;
  expiresAt: number;
  createdBy: string | null;
  total: number;
  used: number;
}

export interface InviteCodeView {
  code: string;
  usedAt: number | null;
  /** "Name (@username)"; null when unused or the user has been deleted. */
  usedBy: string | null;
}

/** Error codes returned by the API as { error: code }. The UI maps them to texts. */
export type ErrorCode =
  | 'invalid_input'
  | 'invalid_credentials'
  | 'account_locked'
  | 'too_many_attempts'
  | 'unauthorized'
  | 'forbidden'
  | 'forbidden_origin'
  | 'not_found'
  | 'username_taken'
  | 'display_name_taken'
  | 'cannot_modify_self'
  | 'cannot_restrict'
  | 'user_busy'
  | 'password_change_required'
  | 'same_password'
  | 'weak_password'
  | 'invalid_link'
  | 'invalid_code'
  | 'registration_closed'
  | 'label_taken'
  | 'name_taken'
  | 'refill_not_available'
  | 'insufficient_coins'
  | 'tables_full'
  | 'table_not_found'
  | 'seat_taken'
  | 'already_seated'
  | 'not_seated'
  | 'not_now'
  | 'bad_bet'
  | 'arena_closed'
  | 'game_running'
  | 'no_game'
  | 'no_players'
  | 'too_fast'
  | 'all_drawn'
  | 'not_bingo'
  | 'batch_in_game'
  | 'auto_game'
  | 'full'
  | 'nickname_required'
  | 'nickname_taken'
  | 'limit_reached'
  | 'player_unknown'
  | 'code_taken'
  | 'code_voided'
  | 'player_busy'
  | 'invalid_phone'
  | 'invalid_email'
  | 'closed'
  | 'phone_limit'
  | 'email_limit'
  | 'ip_limit'
  | 'ip_nickname'
  | 'bot_check'
  | 'no_phone'
  | 'no_mail'
  | 'busy'
  | 'not_enough_codes'
  | 'nothing_selected'
  | 'internal_error';

/** The logged-in user as returned by login and /api/auth/me. */
export interface MeUser extends PublicUser {
  /** New user or password reset by the administrator: must choose an own password first. */
  mustChangePassword: boolean;
  /** Guest (decision 2026-09-29): the visit ends at this time (ms); null for registered users. */
  guestUntil: number | null;
  /** May run the bingo (Bingoisäntä, 2026-10-01); the administrator always may. */
  bingoHost: boolean;
}

export interface HealthResponse {
  status: 'ok' | 'degraded';
  version: string;
  database: 'ok' | 'error';
  serverTime: number;
}

// ---------------------------------------------------------------------------
// Game Arena: games, presence, challenges, game sessions
// ---------------------------------------------------------------------------

/** Game modules available in the arena. Names are UI texts (i18n). */
export const GAME_TYPES = ['battleship', 'othello', 'memory', 'chess', 'snake', 'tetrax'] as const;
export type GameType = (typeof GAME_TYPES)[number];

/**
 * Real-time two-player games (decision 2026-09-30: the snake game) have their own server; the
 * turn-based games use the game modules of the game host.
 */
export const REALTIME_GAME_TYPES = ['snake', 'tetrax'] as const;
export type RealtimeGameType = (typeof REALTIME_GAME_TYPES)[number];
export type TurnGameType = Exclude<GameType, RealtimeGameType>;
export const isRealtimeGame = (g: GameType): g is RealtimeGameType => (REALTIME_GAME_TYPES as readonly string[]).includes(g);
export const TURN_GAME_TYPES = GAME_TYPES.filter((g): g is TurnGameType => !isRealtimeGame(g));

/** Game capabilities shown in the lobby. `solo`: can also be played alone (practice). */
export const GAME_INFO: Record<GameType, { computerOpponent: boolean; solo?: boolean }> = {
  battleship: { computerOpponent: true },
  othello: { computerOpponent: true },
  memory: { computerOpponent: false },
  chess: { computerOpponent: true },
  snake: { computerOpponent: true, solo: true },
  tetrax: { computerOpponent: true, solo: true },
};

/**
 * Chess clock (decision 2026-09-30): the challenger chooses the time before sending the
 * challenge: minutes per player + seconds added after every move.
 */
export interface ChessClock {
  baseMin: number;
  incSec: number;
}
export const CHESS_CLOCKS: readonly ChessClock[] = [
  { baseMin: 3, incSec: 2 },
  { baseMin: 5, incSec: 3 },
  { baseMin: 10, incSec: 5 },
  { baseMin: 15, incSec: 10 },
  { baseMin: 30, incSec: 0 },
];
export const DEFAULT_CHESS_CLOCK: ChessClock = { baseMin: 10, incSec: 5 };
/** Computer strength 1 (beginner) – 5 (strong) (decision 2026-09-30: five levels). */
export const AI_LEVELS = [1, 2, 3, 4, 5] as const;
export const DEFAULT_AI_LEVEL = 2;

/** Choices made before a game (only for the games that have them). */
export interface GameOptions {
  clock?: ChessClock;
  aiLevel?: number;
}

/** waiting: at a game table waiting for a multiplayer game to start (decision 2026-09-27). */
export type PresenceStatus = 'available' | 'idle' | 'in_game' | 'not_accepting' | 'waiting';

export interface LobbyPlayer {
  id: number;
  displayName: string;
  status: PresenceStatus;
  level: ExperienceLevel;
  /** Guest with a guest code (decision 2026-09-29). */
  guest: boolean;
}

export interface PlayerRef {
  id: number;
  displayName: string;
}

export interface ChallengeView {
  id: string;
  gameType: GameType;
  from: PlayerRef;
  to: PlayerRef;
  /** Server time (ms) when the challenge expires. */
  expiresAt: number;
  /** Created by random opponent selection. */
  random: boolean;
  /** Chess: the clock chosen by the challenger. */
  options?: GameOptions;
}

export type ChallengeEndReason = 'accepted' | 'declined' | 'expired' | 'cancelled';

export type GameStatus = 'placement' | 'playing';
export type GameEndReason =
  | 'all_ships_sunk'
  | 'resignation'
  | 'turn_timeout'
  | 'placement_timeout'
  | 'aborted'
  /** Normal end decided by the board (Othello, Memory); without a winner it is a draw. */
  | 'game_complete'
  /** Chess: the players agreed a draw (draw offer accepted, decision 2026-09-30). */
  | 'draw_agreed';

export interface ActiveGameView {
  id: number;
  gameType: GameType;
  status: GameStatus;
  opponent: PlayerRef;
  /** Server time (ms) when fleet placement must be ready. */
  placementDeadline: number | null;
}

export interface GameEndedView {
  id: number;
  gameType: GameType;
  opponent: PlayerRef;
  winnerUserId: number | null;
  reason: GameEndReason;
  /** Snake: how the game ended (a crash, both crashed, time up). */
  snakeEnd?: SnakeEnd;
  tetraxEnd?: TetraxEnd;
  /** Chess: what decided the game on the board (checkmate, stalemate, draw rules). */
  chessEnd?: ChessEnd;
  /** Final score as [mine, opponent's] when the game has one (discs, pairs). */
  score: [number, number] | null;
}

/** Everything the arena needs to know about the current user. Sent on every change. */
export interface MeState {
  status: PresenceStatus;
  acceptsChallenges: boolean;
  activeGame: ActiveGameView | null;
  incoming: ChallengeView[];
  outgoing: ChallengeView | null;
  restrictions: MyRestrictions;
  /** The game table the user is waiting at (multiplayer games). */
  table: TableView | null;
  /** Running multiplayer game of the user. */
  multiGame: { id: number; gameType: MultiGameType } | null;
}

/** Active restrictions of the user (suunnitelma.md section 26). */
export interface MyRestrictions {
  /** Chat mute: null = not muted; `until` null = permanent. */
  chatMute: { until: number | null } | null;
  gameBan: { until: number } | null;
}

export interface GamePlayerView extends PlayerRef {
  /** Setup (e.g. fleet placement) confirmed. */
  ready: boolean;
  /** Connected to the arena right now (always true for the computer). */
  online: boolean;
  /** Computer-controlled player. */
  isAi: boolean;
  /** Personal time bank balance at the start of the player's current/next turn (ms). */
  timeBankMs: number;
}

export interface TurnView {
  userId: number;
  /** Server time (ms) when the turn started. */
  startedAt: number;
}

export interface GameSettingsView {
  turnTimeMs: number;
  timeBankMs: number;
  hitGrantsExtraShot: boolean;
  forbiddenAreaVisible: boolean;
  markSunkSurroundings: boolean;
  /** Chess clock: time added after every move (ms); 0 in other games. */
  incrementMs: number;
  /** Computer strength (chess); null when not chosen. */
  aiLevel: number | null;
}

/** Full state of an active game as one player may see it. `module` is game-specific. */
export interface GameStateView<M = unknown> {
  id: number;
  gameType: GameType;
  status: GameStatus;
  me: GamePlayerView;
  opponent: GamePlayerView;
  placementDeadline: number | null;
  turn: TurnView | null;
  settings: GameSettingsView;
  lastMove: { byUserId: number; data: unknown } | null;
  /** A pending draw offer (chess): who offered. */
  drawOffer: { byUserId: number } | null;
  /** Server time (ms) when the state was sent. */
  serverNow: number;
  module: M;
}

export type ArenaErrorCode =
  | 'invalid_input'
  | 'wrong_phase'
  | 'not_your_turn'
  | 'invalid_move'
  | 'already_shot'
  | 'invalid_setup'
  | 'setup_already_done'
  | 'too_fast'
  | 'chat_closed'
  | 'arena_closed'
  | 'arena_closing'
  | 'message_too_long'
  | 'links_not_allowed'
  | 'chat_muted'
  | 'guest_read_only'
  | 'game_banned'
  | 'not_available'
  | 'self_challenge'
  | 'already_in_game'
  | 'challenge_pending'
  | 'no_opponents'
  | 'arena_full'
  | 'challenge_not_found'
  | 'table_not_found'
  | 'table_full'
  | 'not_table_host'
  | 'already_at_table'
  | 'not_enough_players'
  | 'not_in_game'
  | 'draw_not_available'
  | 'internal_error';

export type ArenaResult<T = object> = ({ ok: true } & T) | { ok: false; error: ArenaErrorCode };
export type Ack<T = object> = (result: ArenaResult<T>) => void;

export interface ServerToClientEvents {
  hello: (payload: { serverTime: number; user: PublicUser }) => void;
  'lobby:players': (players: LobbyPlayer[]) => void;
  'me:state': (me: MeState) => void;
  'challenge:ended': (payload: { challenge: ChallengeView; reason: ChallengeEndReason }) => void;
  'game:ended': (payload: GameEndedView) => void;
  'game:state': (state: GameStateView) => void;
  'chat:message': (payload: { gameId: number; message: ChatMessage }) => void;
  'lobby:message': (message: ChatMessage) => void;
  'lobby:deleted': (payload: { id: number }) => void;
  'lobby:cleared': () => void;
  'lobby:state': (payload: { open: boolean }) => void;
  'arena:closure': (closure: ArenaClosureView) => void;
  /** Automatic logout (2026-10-02): 30 minutes without activity, or away (no connection) for 5 minutes. */
  'session:ended': (payload: { reason: 'idle' | 'away' }) => void;
  'pm:message': (message: PrivateMessage) => void;
  'tables:list': (tables: TableView[]) => void;
  'table:chat': (payload: { tableId: string; message: ChatMessage }) => void;
  'table:closed': (payload: { tableId: string; reason: TableEndReason }) => void;
  'multi:state': (state: MultiGameStateView) => void;
  'multi:ended': (payload: MultiGameEndedView) => void;
  /** Snake (decision 2026-09-30): the state after every step. */
  'snake:state': (state: SnakeGameView) => void;
  'tetrax:state': (state: TetraxGameView) => void;
}

export interface ClientToServerEvents {
  ping: (ack: (payload: { serverTime: number }) => void) => void;
  /** Sent (throttled) when the user interacts with the page; resets the idle timer. */
  activity: () => void;
  'presence:setAccepts': (accepts: boolean, ack: Ack) => void;
  'challenge:send': (payload: { toUserId: number; gameType: GameType; options?: GameOptions }, ack: Ack<{ challenge: ChallengeView }>) => void;
  'challenge:random': (payload: { gameType: GameType; options?: GameOptions }, ack: Ack<{ challenge: ChallengeView }>) => void;
  'challenge:respond': (payload: { id: string; accept: boolean }, ack: Ack) => void;
  'challenge:cancel': (payload: { id: string }, ack: Ack) => void;
  /** Starts a game against the computer. */
  'game:startComputer': (payload: { gameType: GameType; options?: GameOptions }, ack: Ack) => void;
  'game:resign': (ack: Ack) => void;
  /** Chess: offer a draw / answer the opponent's offer (decision 2026-09-30). */
  'game:drawOffer': (ack: Ack<{ declined?: boolean }>) => void;
  'game:drawRespond': (payload: { accept: boolean }, ack: Ack) => void;
  /** Requests the full current game state (e.g. after reconnecting). */
  'game:sync': (ack: Ack<{ state: GameStateView | null }>) => void;
  /** Game-specific setup, e.g. the Battleship fleet. */
  'game:setup': (payload: unknown, ack: Ack) => void;
  /** Game-specific move, e.g. a Battleship shot. */
  'game:move': (payload: unknown, ack: Ack) => void;
  'chat:send': (payload: { text: string }, ack: Ack) => void;
  'chat:history': (ack: Ack<{ messages: ChatMessage[] }>) => void;
  'lobby:send': (payload: { text: string }, ack: Ack) => void;
  'lobby:history': (ack: Ack<{ messages: ChatMessage[]; open: boolean }>) => void;
  'pm:send': (payload: { toUserId: number; text: string }, ack: Ack<{ message: PrivateMessage }>) => void;
  'pm:conversations': (ack: Ack<{ conversations: PmConversation[] }>) => void;
  'pm:history': (payload: { withUserId: number }, ack: Ack<{ messages: PrivateMessage[] }>) => void;
  /** The conversation with this player has been read (decision 2026-09-29). */
  'pm:read': (payload: { withUserId: number }, ack: Ack) => void;
  /** Everyone a private message can be sent to, also players who are not online. */
  'pm:directory': (ack: Ack<{ players: PlayerRef[] }>) => void;
  'table:create': (payload: { gameType: MultiGameType; maxPlayers: number; countdownMin: number; turnTimeS: number }, ack: Ack) => void;
  'table:join': (payload: { id: string }, ack: Ack) => void;
  'table:leave': (ack: Ack) => void;
  'table:start': (ack: Ack) => void;
  'table:addAi': (ack: Ack) => void;
  'table:remove': (payload: { userId: number }, ack: Ack) => void;
  'table:chat': (payload: { text: string }, ack: Ack) => void;
  'multi:move': (payload: unknown, ack: Ack) => void;
  'multi:resign': (ack: Ack) => void;
  'multi:sync': (ack: Ack<{ state: MultiGameStateView | null }>) => void;
  /** Snake: turns are sent without acknowledgement (many per second). */
  'snake:turn': (payload: { dir: SnakeDir }) => void;
  'snake:sync': (ack: Ack<{ state: SnakeGameView | null }>) => void;
  /** Tetrax (2026-10-03): the own board after a change (no acknowledgement). */
  'tetrax:update': (payload: { state: unknown }) => void;
  'tetrax:sync': (ack: Ack<{ state: TetraxGameView | null }>) => void;
}

/** Input limits, shared so that the UI and the server agree. */
export const limits = {
  usernamePattern: '^[a-zA-Z0-9_.-]{3,32}$',
  displayNameMin: 2,
  displayNameMax: 24,
  passwordMin: 8,
  passwordMax: 200,
  chatMessageMax: 300,
} as const;

// ---------------------------------------------------------------------------
// Match chat, statistics, administration
// ---------------------------------------------------------------------------

export interface ChatMessage {
  id: number;
  userId: number;
  displayName: string;
  /** Sender's role (lobby chat), used to colour staff names. */
  role?: Role;
  text: string;
  /** Server time (ms). */
  sentAt: number;
}

export interface GameTypeStats {
  gameType: AnyGameType;
  played: number;
  wins: number;
  losses: number;
  draws: number;
  resignations: number;
  timeouts: number;
  /** Best score (multiplayer games with points, e.g. Nopparalli); null = none. */
  bestScore: number | null;
}

export interface MyStatsResponse {
  total: Omit<GameTypeStats, 'gameType'>;
  byGame: GameTypeStats[];
  level: ExperienceLevel;
  gamesToNextLevel: number | null;
}

/** Administrator-controlled settings (suunnitelma.md section 36). */
export interface GameTimers {
  turnTimeMs: number;
  timeBankMs: number;
}

export interface AdminSettings {
  /** Turn time and time bank per game type (decision 2026-09-27). */
  timers: Record<AnyGameType, GameTimers>;
  placementTimeMs: number;
  startingPlayer: 'challenger' | 'challenged' | 'random';
  maxActiveGames: number;
  hitGrantsExtraShot: boolean;
  forbiddenAreaVisible: boolean;
  markSunkSurroundings: boolean;
  /** null = keep forever. */
  historyRetentionDays: number | null;
  /** Lobby chat messages are kept this long (hours). */
  lobbyChatRetentionHours: number;
}

export interface AdminGameView {
  id: number;
  gameType: AnyGameType;
  status: GameStatus;
  players: (PlayerRef & { isAi: boolean })[];
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Lobby chat, private messages, moderation (suunnitelma.md section 26)
// ---------------------------------------------------------------------------

export interface PrivateMessage {
  id: number;
  from: PlayerRef;
  fromRole: Role;
  to: PlayerRef;
  text: string;
  sentAt: number;
}

export interface PmConversation {
  with: PlayerRef;
  lastAt: number;
  /** Messages from the other player not yet read (decision 2026-09-29). */
  unread: number;
}

export type RestrictionType = 'chat_mute' | 'game_ban';

export interface RestrictionView {
  id: number;
  user: PlayerRef;
  type: RestrictionType;
  /** null = permanent (chat mute only). */
  until: number | null;
  reason: string;
  /** Display name of the setter; null if that account has been deleted. */
  createdBy: string | null;
  createdAt: number;
}

export interface ModUserView extends PlayerRef {
  username: string;
  role: Role;
  restrictions: RestrictionView[];
}

export interface ModLogEntry {
  id: number;
  actor: string;
  action: string;
  target: string | null;
  details: string;
  at: number;
}

export interface AdminPmConversation {
  a: PlayerRef;
  b: PlayerRef;
  count: number;
  lastAt: number;
}

export const isStaff = (role: Role) => role === 'moderator' || role === 'admin';

// ---------------------------------------------------------------------------
// Closing the whole Game Arena, e.g. for maintenance (decision 2026-09-27)
// ---------------------------------------------------------------------------

export interface ArenaClosure {
  /** A closure is set: now, or from `startsAt`. */
  enabled: boolean;
  /** Epoch ms; null = immediately. When the closure starts, all running games are aborted. */
  startsAt: number | null;
  /**
   * Epoch ms from which no new games can be started while running games continue until
   * `startsAt` (a delayed closure, decision 2026-09-27). null = no such phase.
   */
  drainAt: number | null;
  /** Epoch ms when the arena opens again automatically; null = until opened manually. */
  endsAt: number | null;
  /** Shown to the players, e.g. "Huoltotauko". */
  message: string;
}

/**
 * open: normal · upcoming: a closure is scheduled (advance notice) · draining: no new games,
 * running games continue until the closure starts · closed: maintenance page.
 */
export type ClosurePhase = 'open' | 'upcoming' | 'draining' | 'closed';

export interface ArenaClosureView extends ArenaClosure {
  phase: ClosurePhase;
  /** Closed right now (for everyone except the administrator). */
  closed: boolean;
}

export const CLOSURE_MESSAGE_MAX = 200;

export function closurePhase(c: ArenaClosure, now: number): ClosurePhase {
  if (!c.enabled || (c.endsAt !== null && now >= c.endsAt)) return 'open';
  if (c.startsAt === null || now >= c.startsAt) return 'closed';
  if (c.drainAt !== null && now >= c.drainAt) return 'draining';
  return 'upcoming';
}

export function closureActive(c: ArenaClosure, now: number): boolean {
  return closurePhase(c, now) === 'closed';
}

/** A closure is coming (players are told in advance, also during a game). */
export function closureUpcoming(c: ArenaClosure, now: number): boolean {
  const phase = closurePhase(c, now);
  return phase === 'upcoming' || phase === 'draining';
}

// ---------------------------------------------------------------------------
// Multiplayer games: game tables (waiting rooms) and games for 1–6 players
// (decision 2026-09-27). Two-player games keep their own host and challenge flow.
// ---------------------------------------------------------------------------

export const MULTI_GAME_TYPES = ['dice', 'vale'] as const;
export type MultiGameType = (typeof MULTI_GAME_TYPES)[number];
export type AnyGameType = GameType | MultiGameType;
export const ALL_GAME_TYPES: readonly AnyGameType[] = [...GAME_TYPES, ...MULTI_GAME_TYPES];

export const MULTI_GAME_INFO: Record<MultiGameType, { minPlayers: number; maxPlayers: number; defaultMaxPlayers: number }> = {
  dice: { minPlayers: 1, maxPlayers: 6, defaultMaxPlayers: 4 },
  // Valepaskahousu (decision 2026-09-30): 2–6 players, computer players allowed.
  vale: { minPlayers: 2, maxPlayers: 6, defaultMaxPlayers: 4 },
};

/** A table that does not start within this time is removed. */
export const TABLE_TTL_MS = 10 * 60 * 1000;
/** Optional automatic start after this many minutes (0 = host starts). */
export const TABLE_COUNTDOWNS = [0, 2, 5] as const;
/**
 * Thinking time per turn (seconds) chosen by the host when opening a table, per game (decision
 * 2026-09-27; Valepaskahousu 10 s – 2 min, user request 2026-09-30: no long thinking in a bluff game).
 */
export const TABLE_TURN_TIMES_S: Record<MultiGameType, readonly number[]> = {
  dice: [60, 90, 120, 180],
  vale: [10, 15, 20, 30, 45, 60, 90, 120],
};
/** The preselected turn time of each game. */
export const TABLE_DEFAULT_TURN_S: Record<MultiGameType, number> = { dice: 60, vale: 20 };

export interface TableSeat extends PlayerRef {
  isAi: boolean;
}

export interface TableView {
  id: string;
  gameType: MultiGameType;
  host: PlayerRef;
  seats: TableSeat[];
  maxPlayers: number;
  createdAt: number;
  /** Server time (ms) when the table is removed if it has not started. */
  expiresAt: number;
  /** Server time (ms) of the automatic start (countdown), null = the host starts. */
  startsAt: number | null;
  /** Thinking time per turn chosen by the host. */
  turnTimeMs: number;
}

export type TableEndReason = 'started' | 'cancelled' | 'expired' | 'removed';

export interface MultiPlayerView extends PlayerRef {
  isAi: boolean;
  online: boolean;
  /** Left the game (resigned or removed after timeouts); the game continues for the others. */
  dropped: boolean;
  timeBankMs: number;
}

/** Full state of a multiplayer game as one player sees it. Players are in turn order. */
export interface MultiGameStateView<M = unknown> {
  id: number;
  gameType: MultiGameType;
  players: MultiPlayerView[];
  turn: TurnView | null;
  settings: GameTimers;
  serverNow: number;
  module: M;
}

export type MultiEndReason = 'game_complete' | 'players_left' | 'aborted';

export interface MultiStanding {
  player: TableSeat;
  score: number;
  /** 1 = winner; equal scores share the placement. Players who left are placed last. */
  placement: number;
  dropped: boolean;
}

export interface MultiGameEndedView {
  id: number;
  gameType: MultiGameType;
  reason: MultiEndReason;
  standings: MultiStanding[];
}
